import { Injectable, Output, EventEmitter } from "@angular/core";
import { MatDialog } from "@angular/material";
import { Subject } from "rxjs";

import { SAML, createSamlIdentityProvider } from "@impulse/sdp-shared-models";

import { ApiGatewayService } from "../../../common/api-gateway/api-gateway.service";
import { SamlIdpAttributes } from "./models";
import { TranslateService } from "../../../common/translate/translate.service";
import { SnackbarService } from "../../../common/snackbar/snackbar.service";

@Injectable()
export class SamlWizardService {
	@Output() public submitSuccessEvent: EventEmitter<string> = new EventEmitter(true);

	private _optionData: string[] = [];
	private _identityProvider!: SAML.IdentityProvider;
	private idpsChange = new Subject<SAML.IdentityProvider[]>();
	private _isCreate = false;

	constructor(
		public dialog: MatDialog,
		private apiGatewayService: ApiGatewayService,
		private translateService: TranslateService,
		private snackbarService: SnackbarService
	) { }

	public async getIdentityProviderOptions() {
		if (!this._optionData || this._optionData.length === 0) {
			this._optionData = await this.apiGatewayService.getIdentityProviderOptions();
		}

		return this._optionData;
	}

	public async getIdentityProviderOption(value: string) {
		return await this.apiGatewayService.getIdentityProviderOption(value);
	}

	public async getIdentityProviders() {
		const idps = await this.apiGatewayService.getIdentityProviders();
		this.idpsChange.next(idps);
		return idps;
	}

	public get identityProvider() {
		return this._identityProvider;
	}

	public get identityProvidersSubject() {
		return this.idpsChange;
	}

	public get isCreate() {
		return this._isCreate;
	}

	public newIdentityProvider() {
		this._isCreate = true;
		this._identityProvider = createSamlIdentityProvider("", "", "", []);
	}

	public editIdentityProvider(identityProvider: SAML.IdentityProvider) {
		this._isCreate = false;
		this._identityProvider = identityProvider;
	}

	public processSelectIdp(idpName: string) {
		this._identityProvider.name = idpName;
	}

	public processUpload(value: string) {
		if (value.length > 0) {
			this._identityProvider.metadata = value;
		}
	}

	public processUsername(value: SamlIdpAttributes) {
		Object.assign(this._identityProvider, value);
	}

	public async processSummary() {
		try {
			let idps: SAML.IdentityProvider[];
			let successMessage: string;
			if (this._isCreate) {
				idps = await this.apiGatewayService.createSamlIdentityProvider(this._identityProvider);
				successMessage = "saml.create.idp.success";
			} else {
				idps = await this.apiGatewayService.updateSamlIdentityProvider(this._identityProvider, this._identityProvider.id);
				successMessage = "saml.update.idp.success";
			}
			this.idpsChange.next(idps);
			this.closeDialog();
			this.snackbarService.open(
				this.translateService.translate(
					successMessage,
					{
						idpName: this._identityProvider.name
					}
				)
			);
		} catch (e) {
			this.snackbarService.open(this.translateService.translate(e));
		}
	}

	public async deleteIdentityProvider(id: string) {
		const idps = await this.apiGatewayService.deleteIdentityProvider(id);
		this.idpsChange.next(idps);
	}

	public getUrl(url: string) {
		return this.apiGatewayService.getUrl(url);
	}

	private closeDialog() {
		this.dialog.closeAll();
	}
}
